// Вставлення рядків ПН з таблиці (Excel, Google Таблиці, облікова програма): текст із табуляціями →
// попередній перегляд із зіставленням колонок, номерами вихідних рядків, пропущеними рядками й проблемами.
// Нічого не вгадуємо мовчки: заголовок і «Разом» пропускаємо з причиною, ставку беремо лише з колонки
// або з явного вибору користувача, сумнівне (0% без уточнення, не число) — проблема рядка, а не виправлення.
import { RATES, parseDec } from "./calc.js";
import { newRow, SERVICE_UNIT } from "./model.js";
import { UKTZED, DKPP } from "./validate.js";

// Поля, які можна призначити колонці
export const PASTE_FIELDS = {
  name: "Назва",
  code: "Код УКТ ЗЕД / ДКПП",
  unit: "Одиниця",
  qty: "Кількість",
  price: "Ціна",
  sum: "Сума рядка",
  rate: "Ставка ПДВ",
  imported: "Імпорт (1 — так)",
  skip: "Не брати",
};
const HEADER = {
  name: /^(назва|найменування|товар|послуга|опис)/i,
  code: /^(код|укт\s?зед|уктзед|дкпп)/i,
  unit: /^(од\.?|одиниц|од\.\s?вим)/i,
  qty: /^(к-?сть|кількість|кіл\.?)/i,
  price: /^(ціна)/i,
  sum: /^(сума|вартість)/i,
  rate: /^(ставка|пдв\s?%|%\s?пдв)/i,
  imported: /^(імпорт|ввезен|ознака\s?імп)/i,
};
const DEFAULT_ORDER = ["name", "code", "unit", "qty", "price"];
// Колонка імпорту — лише одразу після коду: так «1» не сплутати з кількістю чи частиною назви
const IMPORT_ORDER = ["name", "code", "imported", "unit", "qty", "price"];
const IMPORT_NOTE = "Імпорт враховуємо лише в колонці одразу після коду УКТ ЗЕД — переставте колонку в таблиці або призначте її після коду";

// Імпорт не одразу після коду — не беремо (і пояснюємо)
export function normalizeMapping(mapping) {
  let note = null;
  const m = mapping.map((k, i) => {
    if (k !== "imported" || mapping[i - 1] === "code") return k;
    note = IMPORT_NOTE; return "skip";
  });
  return { mapping: m, note };
}
// Чи може колонка i бути імпортом (для вибору в перегляді)
export const importAllowed = (mapping, i) => mapping[i - 1] === "code";
const TOTAL = /^(разом|всього|усього|итого|підсумок)/i;

// Рядки тексту → комірки; номер рядка — як у вставленому тексті (з 1)
export function splitPaste(text) {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
  if (lines.at(-1) === "") lines.pop(); // кінцевий перенос після копіювання з Excel
  return lines.map((l, i) => ({ line: i + 1, cells: l.split("\t").map((c) => c.trim()) }));
}

const matchHeader = (cell) => Object.keys(HEADER).find((k) => HEADER[k].test(cell)) || null;

// Зіставлення колонок: за рядком заголовків (якщо в першому непорожньому рядку ≥ 2 впізнаних назв),
// інакше — за звичним порядком «назва · код · одиниця · кількість · ціна», решта колонок — «не брати»
export function guessMapping(rows) {
  const first = rows.find((r) => r.cells.some(Boolean));
  const width = Math.max(0, ...rows.map((r) => r.cells.length));
  if (first) {
    const m = first.cells.map(matchHeader);
    if (m.filter(Boolean).length >= 2) {
      const used = new Set();
      return { headerLine: first.line, ...normalizeMapping(Array.from({ length: width }, (_, i) => {
        const k = m[i];
        if (!k || used.has(k)) return "skip";
        used.add(k); return k;
      })) };
    }
  }
  // Без заголовка: третя колонка — імпорт, лише якщо колонок ≥ 6 і в ній самі «1», «0» чи порожньо (хоч одна «1»)
  const third = rows.filter((r) => r.cells.some(Boolean)).map((r) => r.cells[2] ?? "");
  const order = width >= 6 && third.some((v) => v === "1") && third.every((v) => ["", "0", "1"].includes(v)) ? IMPORT_ORDER : DEFAULT_ORDER;
  return { headerLine: null, note: null, mapping: Array.from({ length: width }, (_, i) => order[i] || "skip") };
}

// «20», «20%», «20 %» → 20; «7»; «14»; «звільнено», «без ПДВ» → 903; «0», «0%» — потрібне уточнення
export function parseRate(s) {
  const t = String(s || "").trim().toLowerCase().replace(/\s/g, "");
  if (!t) return { rate: "" };
  if (/^(звільн|безпдв|непдв|необ)/.test(t)) return { rate: "903" };
  if (/^експорт/.test(t)) return { rate: "901" };
  const n = t.replace(/%$/, "").replace(",", ".");
  if (["20", "7", "14"].includes(String(Number(n))) && /^\d+(\.0+)?$/.test(n)) return { rate: String(Number(n)) };
  if (/^0(\.0+)?$/.test(n)) return { rate: "", problem: "ставка 0% — оберіть: експорт чи на митній території" };
  return { rate: "", problem: `ставка «${s}» — не впізнано (20, 7, 14 або «звільнено»)` };
}
// Ознака імпорту (ввезений на митну територію України товар, у ПН — RXXXXG32): лише «1» — так; порожньо чи «0» — ні.
// «так», «+» тощо не приймаємо — проблема рядка, а не здогадка
export function parseFlag(s) {
  const t = String(s || "").trim();
  if (t === "1") return { value: true };
  if (t === "" || t === "0") return { value: false };
  return { value: false, problem: `імпорт «${s}» — пишіть 1 (товар ввезено) або залиште порожнім` };
}
const num = (s) => String(s || "").replace(/[\s ]/g, "");

// Рядки для додавання + пропущені з причиною. defaultRate — явний вибір користувача для рядків без ставки
export function buildPasteRows(rows, { mapping: raw, headerLine }, { defaultRate = "" } = {}) {
  const { mapping } = normalizeMapping(raw);
  const col = (k) => mapping.indexOf(k);
  const get = (r, k) => (col(k) >= 0 ? r.cells[col(k)] ?? "" : "");
  const out = [], skipped = [];
  let empty = 0;
  for (const r of rows) {
    if (!r.cells.some(Boolean)) { empty++; continue; }
    if (r.line === headerLine) { skipped.push({ line: r.line, reason: "рядок заголовків", text: r.cells.join(" · ") }); continue; }
    if (r.cells.some((c) => TOTAL.test(c))) { skipped.push({ line: r.line, reason: "підсумковий рядок", text: r.cells.filter(Boolean).join(" · ") }); continue; }
    const problems = [];
    const name = get(r, "name");
    if (!name) problems.push("немає назви");
    const code = num(get(r, "code"));
    const service = /^\d{2}\.\d{2}/.test(code);
    if (code && !(service ? DKPP.test(code) : UKTZED.test(code))) problems.push(`код «${get(r, "code")}» — не схожий на УКТ ЗЕД (4–10 цифр) чи ДКПП (43.21.10)`);
    const qty = num(get(r, "qty")) || "1";
    if (parseDec(qty) == null) problems.push(`кількість «${get(r, "qty")}» — не число`);
    const price = num(get(r, "price")), sum = num(get(r, "sum"));
    if (price && parseDec(price) == null) problems.push(`ціна «${get(r, "price")}» — не число`);
    if (sum && parseDec(sum) == null) problems.push(`сума «${get(r, "sum")}» — не число`);
    if (!price && !sum) problems.push("немає ні ціни, ні суми");
    const fromCol = col("rate") >= 0 ? parseRate(get(r, "rate")) : { rate: "" };
    if (fromCol.problem) problems.push(fromCol.problem);
    const rate = fromCol.rate || (fromCol.problem ? "" : defaultRate && defaultRate in RATES ? defaultRate : "");
    const unit = get(r, "unit");
    const imp = col("imported") >= 0 ? parseFlag(get(r, "imported")) : { value: false };
    if (imp.problem) problems.push(imp.problem);
    if (imp.value && service) problems.push("послуга не може бути імпортом — приберіть позначку або вкажіть код товару");
    else if (imp.value && code.length !== 10) problems.push("ввезений товар — потрібен повний код УКТ ЗЕД, 10 цифр");
    out.push({
      line: r.line, problems, rateFrom: fromCol.rate ? "column" : rate ? "default" : null,
      row: newRow({
        name, type: service ? "service" : "goods",
        ...(service ? { dkpp: code, unit: SERVICE_UNIT } : { uktzed: code, unit: unit || "шт", imported: imp.value }),
        qty, price, sum, amountBy: !price && sum ? "sum" : "price", rate,
        source: `вставлено з таблиці, рядок ${r.line}`,
      }),
    });
  }
  return { rows: out, skipped, empty };
}
